import {
  rotuloDaCategoriaDoWorkItem,
  rotuloDaSaudeDoPrazo,
  rotuloDaSituacao,
  tokenDaCategoriaDoWorkItem,
  tokenDaSituacao,
  tokenDoPrazo,
} from '@/features/acoes/rotulos';
import type { EstadoDoWorkItem, SaudePrazo, SituacaoAcao } from '@/types/api';

import estilos from './Selos.module.css';

/**
 * Selos de situacao e de prazo.
 *
 * A COR NUNCA CARREGA A INFORMACAO SOZINHA. Cada selo tem rotulo em texto, e o
 * ponto colorido e reforco. Isso e requisito de acessibilidade (WCAG 1.4.1) e
 * tambem pratico: o painel e impresso e enviado em captura de tela.
 */

interface SeloDeSituacaoProps {
  situacao: SituacaoAcao;
}

export function SeloDeSituacao({ situacao }: SeloDeSituacaoProps) {
  return (
    <span className={estilos.selo}>
      <span
        className={estilos.ponto}
        style={{ background: `var(${tokenDaSituacao[situacao]})` }}
        aria-hidden="true"
      />
      {rotuloDaSituacao[situacao]}
    </span>
  );
}

interface SeloDoWorkItemProps {
  /** Estado no Azure, ou `undefined` quando nao ha estado para esta task. */
  estado: EstadoDoWorkItem | undefined;
  /** `false` quando o Azure nao respondeu. */
  disponivel: boolean;
}

/**
 * Estado da task no Azure DevOps, resumido.
 *
 * TRES CASOS QUE NAO PODEM PARECER IGUAIS:
 *
 * Azure inacessivel        "status indisponível", em tom neutro. Nao e a task que sumiu.
 * task sem estado          nada. O work item foi omitido pela API, provavelmente excluido.
 * estado conhecido         a categoria, com o texto cru no `title`.
 *
 * A CATEGORIA E O ROTULO, e nao o estado cru. Sao tres valores em vez de seis ou mais, que e
 * o que voce pediu ao dizer "mais resumido do que todos". Quando a categoria nao vem — estado
 * fora do catalogo, caso real deste projeto com `Design` e `To Do` — cai para o texto cru em
 * selo neutro, porque chutar o balde seria pior do que mostrar o que o Azure disse.
 */
export function SeloDoWorkItem({ estado, disponivel }: SeloDoWorkItemProps) {
  if (!disponivel) {
    return (
      <span className={estilos.selo} title="Não foi possível consultar o Azure DevOps agora.">
        status indisponível
      </span>
    );
  }

  if (estado === undefined) {
    return null;
  }

  const categoria = estado.categoria;
  const detalhe =
    `${estado.tipo} · ${estado.estado}` +
    (estado.atribuidoA === null ? '' : ` · ${estado.atribuidoA}`);

  return (
    <span
      className={estilos.selo}
      title={estado.titulo === null ? detalhe : `${detalhe}\n${estado.titulo}`}
    >
      {categoria === null ? null : (
        <span
          className={estilos.ponto}
          style={{ background: `var(${tokenDaCategoriaDoWorkItem[categoria]})` }}
          aria-hidden="true"
        />
      )}
      {categoria === null ? estado.estado : rotuloDaCategoriaDoWorkItem[categoria]}
    </span>
  );
}

interface SeloDePrazoProps {
  saude: SaudePrazo;
  prazo: string | null;
  /** Dias restantes, quando a tela quiser ser especifica. */
  diasRestantes?: number | null;
}

export function SeloDePrazo({ saude, prazo, diasRestantes }: SeloDePrazoProps) {
  // Sem prazo e encerrada nao ganham selo: seriam ruido em uma coluna onde a
  // ausencia ja e visivel pelo traco na data.
  if (saude === 'SemPrazo' || saude === 'NaoAplicavel' || prazo === null) {
    return null;
  }

  const classe =
    saude === 'Atrasado'
      ? `${estilos.selo} ${estilos.prazoAtrasado}`
      : saude === 'ProximoVencimento'
        ? `${estilos.selo} ${estilos.prazoProximo}`
        : estilos.selo;

  return (
    <span className={classe}>
      <span
        className={estilos.ponto}
        style={{ background: `var(${tokenDoPrazo[saude]})` }}
        aria-hidden="true"
      />
      {descrever(saude, diasRestantes)}
    </span>
  );
}

/**
 * Texto do selo de prazo.
 *
 * "Atrasada 12 dias" em vez de "Critico": o guia de identidade pede linguagem
 * factual e desaconselha tom alarmante. O numero informa mais do que o adjetivo.
 */
function descrever(saude: SaudePrazo, diasRestantes: number | null | undefined): string {
  if (diasRestantes === null || diasRestantes === undefined) {
    return rotuloDaSaudeDoPrazo[saude];
  }

  if (saude === 'Atrasado') {
    const dias = Math.abs(diasRestantes);

    return dias === 1 ? 'Atrasada 1 dia' : `Atrasada ${String(dias)} dias`;
  }

  if (saude === 'ProximoVencimento') {
    if (diasRestantes === 0) {
      return 'Vence hoje';
    }

    return diasRestantes === 1 ? 'Vence amanha' : `Vence em ${String(diasRestantes)} dias`;
  }

  return rotuloDaSaudeDoPrazo[saude];
}

interface SeloDeImpedimentoProps {
  visivel: boolean;
}

export function SeloDeImpedimento({ visivel }: SeloDeImpedimentoProps) {
  if (!visivel) {
    return null;
  }

  return <span className={`${estilos.selo} ${estilos.impedimento}`}>Impedida</span>;
}
